import {
  EditorView,
  Decoration,
  ViewPlugin,
  DecorationSet,
  ViewUpdate,
} from "@codemirror/view";
import { IHightlighType } from "../renderer/elements/Quiz/types";

export type LineRange = {
  line: number;
  endLine?: number; // for line ranges
  columnStart?: number; // start column
  columnEnd?: number; // end column
};

export type HighlightNumberType = {
  explanation: string;
  lines: (number | LineRange)[]; // can be either line number or line range
};

export type LineHighlight = {
  lines: (number | LineRange)[];
  lineStyle?: IHightlighType;
  markStyle?: IHightlighType;
  className?: string;
};

export const getSingleLineStyls = (hightlight: IHightlighType) => {
  if (hightlight === "neutral") {
    return "background-color: rgba(147, 100, 237, 0.2) ; border-top: 2px solid #9364ED !important;  border-bottom: 2px solid #9364ED !important";
  } else if (hightlight === "success") {
    return "background-color: rgba(76, 175, 80, 0.15); border-top: 2px solid #4AB481 !important;  border-bottom: 2px solid #4AB481 !important";
  } else if (hightlight === "error") {
    return "background-color: rgba(239, 58, 124, 0.15); border-top: 2px solid #EF3A7C !important;  border-bottom: 2px solid #EF3A7C !important";
  } else {
    return "background-color: rgba(255, 193, 7, 0.1); border-top: 2px solid #FACC15 !important;  border-bottom: 2px solid #FACC15 !important";
  }
};

const getBgColor = (hightlight: IHightlighType) => {
  if (hightlight === "neutral") {
    return "rgba(147, 100, 237, 0.2) ;";
  } else if (hightlight === "success") {
    return "rgba(76, 175, 80, 0.15);";
  } else if (hightlight === "error") {
    return "rgba(239, 58, 124, 0.15);";
  } else {
    return "rgba(255, 193, 7, 0.1);";
  }
};

export const getColor = (hightlight: IHightlighType) => {
  if (hightlight === "neutral") {
    return "#9364ED";
  } else if (hightlight === "success") {
    return "#4AB481";
  } else if (hightlight === "error") {
    return "#EF3A7C";
  } else {
    return "#FACC15";
  }
};

export const getMarkStyle = (hightlight: IHightlighType) => {
  if (hightlight === "neutral") {
    return "background-color: rgba(147, 100, 237, 0.2); border: 2px solid #9364ED; border-radius: .3rem; padding-top: .15rem; padding-bottom: .15rem; ";
  } else if (hightlight === "success") {
    return "background-color: rgba(76, 175, 80, 0.2); border: 2px solid #4AB481; border-radius: .3rem; padding-top: .15rem; padding-bottom: .15rem; ";
  } else if (hightlight === "error") {
    return "background-color: rgba(239, 58, 124, 0.15); border: 2px solid #EF3A7C; border-radius: .3rem; padding-top: .15rem; padding-bottom: .15rem; ";
  } else {
    return "background-color: rgba(255, 193, 7, 0.1); border: 2px solid #FACC15; border-radius: .3rem; padding-top: .15rem; padding-bottom: .15rem;";
  }
};

// const highlight: LineHighlight = {
//   lines: [
//     // Single line
//     5,
//     // Line range
//     { line: 10, endLine: 15 },
//     // Column range within a line
//     { line: 20, start: 5, end: 10 }
//   ],
//   lineStyle: "your-style-here"
// };

export const createHighlightPlugin = (
  getHighlight: () => LineHighlight | null,
  defaultLineHightlight: IHightlighType = "neutral",
  defaultColumnHighlight: IHightlighType = "neutral"
) => {
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = this.getDecorations(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = this.getDecorations(update.view);
        }
      }

      getDecorations(view: EditorView) {
        const highlight = getHighlight();
        const decorations = [];

        // Add transparent borders to all lines first
        for (let lineNo = 1; lineNo <= view.state.doc.lines; lineNo++) {
          const line = view.state.doc.line(lineNo);
          decorations.push(
            Decoration.line({
              attributes: {
                style:
                  "border-top: 2px solid transparent; border-bottom: 2px solid transparent;",
              },
            }).range(line.from)
          );
        }

        if (highlight?.lines) {
          for (const lineInfo of highlight.lines) {
            try {
              if (typeof lineInfo === "number") {
                // Single line highlight
                const line = view.state.doc.line(Math.max(1, lineInfo));
                decorations.unshift(
                  Decoration.line({
                    attributes: highlight.className
                      ? { class: highlight.className }
                      : {
                          style: getSingleLineStyls(
                            highlight.lineStyle || defaultColumnHighlight
                          ),
                        },
                  }).range(line.from)
                );
              } else if (lineInfo.endLine) {
                // Line range highlight
                const startLine = Math.max(1, lineInfo.line);
                const endLine = Math.max(startLine, lineInfo.endLine);

                for (let lineNum = startLine; lineNum <= endLine; lineNum++) {
                  const line = view.state.doc.line(lineNum);
                  let style = `background-color: ${getBgColor(
                    highlight.lineStyle || defaultLineHightlight
                  )};`;

                  if (lineNum === startLine) {
                    style = `${style} border-top: 2px solid ${getColor(
                      highlight.lineStyle || defaultLineHightlight
                    )} !important; border-bottom: none;`;
                  } else if (lineNum === endLine) {
                    style = `${style} border-bottom: 2px solid ${getColor(
                      highlight.lineStyle || defaultLineHightlight
                    )} !important; border-top: none;`;
                  } else {
                    style = `${style} border-top: none; border-bottom: none;`;
                  }

                  decorations.unshift(
                    Decoration.line({
                      attributes: { style },
                    }).range(line.from)
                  );
                }
              } else if (
                lineInfo.columnStart !== undefined &&
                lineInfo.columnEnd !== undefined
              ) {
                // Column range within a line
                const line = view.state.doc.line(lineInfo.line);
                const start = line.from + lineInfo.columnStart;
                const end = line.from + lineInfo.columnEnd;

                decorations.push(
                  Decoration.mark({
                    attributes: highlight.className
                      ? { class: highlight.className }
                      : {
                          style: highlight.markStyle
                            ? getMarkStyle(highlight.markStyle)
                            : getMarkStyle(defaultColumnHighlight),
                        },
                  }).range(start, end)
                );
              }
            } catch (e) {
              console.warn(`Invalid line/column range:`, lineInfo);
            }
          }
        }

        return Decoration.set(decorations, true);
      }
    },
    {
      decorations: (v) => v.decorations,
    }
  );
};
